import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc,
  deleteDoc,
  query,
  where,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '../config'
import type { Organization } from '@/types/organization'

// Interface para associação usuário-organização
export interface UserOrganization {
  id?: string
  userId: string
  organizationId: string
  role: 'admin' | 'user'
  createdAt?: string
  updatedAt?: string
}

// Função auxiliar para converter timestamp para ISO string
const convertTimestampToISOString = (timestamp: any): string | undefined => {
  if (!timestamp) return undefined
  // Se já for string, retorna como está
  if (typeof timestamp === 'string') return timestamp
  // Se for timestamp do Firestore
  if (timestamp?.toDate instanceof Function) return timestamp.toDate().toISOString()
  // Se for Date
  if (timestamp instanceof Date) return timestamp.toISOString()
  // Se for timestamp em segundos
  if (typeof timestamp === 'number') return new Date(timestamp * 1000).toISOString()
  return undefined
}

// Busca todas as organizações que o usuário tem acesso
export const getUserOrganizations = async (userId: string) => {
  try {
    // Se for o admin universal, busca todas as organizações
    if (userId === 'ZPVelJ5hXXV6rMmYdNxgVOwSCHA2') {
      const orgsRef = collection(db, 'organizations')
      const orgsSnapshot = await getDocs(orgsRef)
      
      const orgs = orgsSnapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          ...data,
          createdAt: convertTimestampToISOString(data.createdAt),
          updatedAt: convertTimestampToISOString(data.updatedAt),
          role: 'admin' // Admin universal sempre tem role admin
        }
      })
      
      return orgs as (Organization & { role: string })[]
    }

    // Para outros usuários, busca as associações normalmente
    const userOrgsRef = collection(db, 'user-organizations')
    const q = query(userOrgsRef, where('userId', '==', userId))
    const userOrgsSnapshot = await getDocs(q)
    
    // Se não tem associações, retorna array vazio
    if (userOrgsSnapshot.empty) {
      return []
    }

    // Mapeia as associações
    const userOrgs = userOrgsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: convertTimestampToISOString(doc.data().createdAt),
      updatedAt: convertTimestampToISOString(doc.data().updatedAt)
    })) as UserOrganization[]

    // Busca os detalhes de cada organização
    const orgsPromises = userOrgs.map(async userOrg => {
      const orgDoc = await getDoc(doc(db, 'organizations', userOrg.organizationId))
      if (!orgDoc.exists()) return null
      
      const data = orgDoc.data()
      return {
        id: orgDoc.id,
        ...data,
        createdAt: convertTimestampToISOString(data.createdAt),
        updatedAt: convertTimestampToISOString(data.updatedAt),
        role: userOrg.role
      }
    })

    const orgs = await Promise.all(orgsPromises)
    return orgs.filter(org => org !== null) as (Organization & { role: string })[]
  } catch (error) {
    console.error('[organizations.service] Error getting user organizations:', error)
    throw error
  }
}

// Cria uma nova organização e associa ao usuário criador como admin
export const createOrganization = async (userId: string, data: Omit<Organization, 'id' | 'createdAt' | 'updatedAt'>) => {
  const batch = writeBatch(db)
  
  try {
    // Cria a organização
    const orgRef = collection(db, 'organizations')
    const newOrg = {
      ...data,
      modules: {
        accounts: true,
        banks: true,
        cashFlow: true,
        costCenters: true,
        customers: true,
        dashboard: true,
        suppliers: true,
        ...data.modules // Permite sobrescrever os valores padrão
      },
      settings: {
        fiscalYear: {
          startMonth: 1,
          endMonth: 12,
          ...data.settings?.fiscalYear
        },
        currency: 'BRL',
        timezone: 'America/Sao_Paulo',
        ...data.settings
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }
    const orgDoc = await addDoc(orgRef, newOrg)

    // Cria a associação usuário-organização
    const userOrgRef = collection(db, 'user-organizations')
    const userOrg = {
      userId,
      organizationId: orgDoc.id,
      role: 'admin',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }
    await addDoc(userOrgRef, userOrg)

    // Comita as alterações
    await batch.commit()

    return {
      id: orgDoc.id,
      ...newOrg,
      role: 'admin'
    }
  } catch (error) {
    console.error('[organizations.service] Error creating organization:', error)
    throw error
  }
}

// Atualiza uma organização
export const updateOrganization = async (id: string, data: Partial<Organization>) => {
  try {
    const orgRef = doc(db, 'organizations', id)
    await updateDoc(orgRef, {
      ...data,
      updatedAt: serverTimestamp()
    })
  } catch (error) {
    console.error('[organizations.service] Error updating organization:', error)
    throw error
  }
}

// Deleta uma organização e todas as suas associações
export const deleteOrganization = async (id: string) => {
  const batch = writeBatch(db)
  
  try {
    // Deleta a organização
    const orgRef = doc(db, 'organizations', id)
    batch.delete(orgRef)

    // Busca e deleta todas as associações
    const userOrgsRef = collection(db, 'user-organizations')
    const q = query(userOrgsRef, where('organizationId', '==', id))
    const userOrgsSnapshot = await getDocs(q)
    
    userOrgsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref)
    })

    // Comita as alterações
    await batch.commit()
  } catch (error) {
    console.error('[organizations.service] Error deleting organization:', error)
    throw error
  }
}

// Adiciona um usuário a uma organização
export const addUserToOrganization = async (userId: string, organizationId: string, role: 'admin' | 'user' = 'user') => {
  try {
    const userOrgRef = collection(db, 'user-organizations')
    const userOrg = {
      userId,
      organizationId,
      role,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }
    await addDoc(userOrgRef, userOrg)
  } catch (error) {
    console.error('[organizations.service] Error adding user to organization:', error)
    throw error
  }
}

// Remove um usuário de uma organização
export const removeUserFromOrganization = async (userId: string, organizationId: string) => {
  try {
    const userOrgsRef = collection(db, 'user-organizations')
    const q = query(
      userOrgsRef, 
      where('userId', '==', userId),
      where('organizationId', '==', organizationId)
    )
    const snapshot = await getDocs(q)
    
    if (!snapshot.empty) {
      await deleteDoc(snapshot.docs[0].ref)
    }
  } catch (error) {
    console.error('[organizations.service] Error removing user from organization:', error)
    throw error
  }
}

// Atualiza o papel do usuário em uma organização
export const updateUserRole = async (userId: string, organizationId: string, role: 'admin' | 'user') => {
  try {
    const userOrgsRef = collection(db, 'user-organizations')
    const q = query(
      userOrgsRef, 
      where('userId', '==', userId),
      where('organizationId', '==', organizationId)
    )
    const snapshot = await getDocs(q)
    
    if (!snapshot.empty) {
      await updateDoc(snapshot.docs[0].ref, {
        role,
        updatedAt: serverTimestamp()
      })
    }
  } catch (error) {
    console.error('[organizations.service] Error updating user role:', error)
    throw error
  }
}
